import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  AdminCheckInOutHistory,
  resetEmployeeAttendace,
} from "../../../services/Services";
import {
  generateErrorMessage,
  generateSuccessMessage,
} from "../../../utils/GenerateMessage";
import { Box, Dialog, Popover, Skeleton, Tooltip } from "@mui/material";
import { convertedDate } from "app/utils/utils";
import * as FileSaver from "file-saver";
import * as XLSX from "xlsx";
import * as moment from "moment";
import { baseURL } from "app/Api/axios";
import useEmpList from "app/hooks/useEmpList";
import { COLORS, FONTS, SHADOWS, RADII, getInitials, TINTS } from "app/views/dashboard/shared/tokens";
import { TABLE_CARD_SX, STATUS_COLORS, DEFAULT_SHIFT } from "../AdminList";

/* ─── Placeholder for broken photos ─── */
const PHOTO_PLACEHOLDER = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='50' height='50' viewBox='0 0 50 50'%3E%3Crect fill='%23f1edf8' width='50' height='50' rx='8'/%3E%3Ctext x='25' y='30' text-anchor='middle' fill='%23b3aac4' font-size='10' font-family='sans-serif'%3ENo img%3C/text%3E%3C/svg%3E";

/* ─── Status derivation (same logic as daily) ─── */
function deriveStatus(row, shiftConfig) {
  if (row.leaveStatus === "approved" || row.leaveStatus === "Approved" || row.isOnLeave) return "onleave";
  if (!row.checkInTime && !row.checkOutTime) return "absent";
  if (row.checkInTime && !row.checkOutTime) return "nocheckout";
  const { startHour, startMin, graceMin } = shiftConfig;
  const shiftStartTotal = startHour * 60 + startMin + graceMin;
  const inParts = row.checkInTime?.split(":") || [];
  if (inParts.length >= 2) {
    let inH, inM;
    if (row.checkInTime.match(/AM|PM/i)) {
      const d = new Date(`1/1/2000 ${row.checkInTime}`);
      inH = d.getHours(); inM = d.getMinutes();
    } else {
      inH = parseInt(inParts[0], 10); inM = parseInt(inParts[1], 10);
    }
    if (inH * 60 + inM > shiftStartTotal) return "late";
  }
  return "ontime";
}

function getLateMins(row, shiftConfig) {
  const { startHour, startMin } = shiftConfig;
  const shiftStartTotal = startHour * 60 + startMin;
  const inParts = row.checkInTime?.split(":") || [];
  if (inParts.length < 2) return 0;
  let inH, inM;
  if (row.checkInTime.match(/AM|PM/i)) {
    const d = new Date(`1/1/2000 ${row.checkInTime}`);
    inH = d.getHours(); inM = d.getMinutes();
  } else {
    inH = parseInt(inParts[0], 10); inM = parseInt(inParts[1], 10);
  }
  return Math.max(0, inH * 60 + inM - shiftStartTotal);
}

function parseHours(val) {
  if (!val) return 0;
  if (typeof val === "number") return val;
  const s = String(val).trim();
  const hm = s.match(/(\d+)\s*h\s*(\d+)/i);
  if (hm) return parseInt(hm[1]) + parseInt(hm[2]) / 60;
  const parts = s.split(":");
  if (parts.length >= 2) return parseInt(parts[0]) + parseInt(parts[1]) / 60;
  return parseFloat(s) || 0;
}

/* ─── Styles ─── */
const HEAD_CELL = {
  fontSize: "11.5px", fontWeight: 700, letterSpacing: "0.06em",
  textTransform: "uppercase", color: COLORS.muted, userSelect: "none",
};
const GRID_COLS = "2.3fr 1fr 1.35fr 0.85fr 0.85fr 1.4fr 88px 58px 104px";
const ROW_HOVER = { "&:hover": { background: "#faf8fd" }, "&:hover .tt-actions": { opacity: 1 } };

function chipSx(status) {
  const c = STATUS_COLORS[status] || STATUS_COLORS.absent;
  return {
    display: "inline-flex", alignItems: "center", fontSize: "12.5px", fontWeight: 600,
    px: "10px", py: "4px", borderRadius: "7px", whiteSpace: "nowrap",
    color: c.ink, background: c.bg, border: `1px solid ${c.border}`,
  };
}
const STATUS_LABELS = {
  ontime: "On time", late: "Late", nocheckout: "No check-out",
  absent: "Absent", onleave: "On leave",
};

const MonthlyList = ({
  activeDate,
  activeFilters,
  onStatCounts,
  onTotalCount,
  onClearFilters,
  config,
  exportRef,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [tableResponse, setTableResponse] = useState([]);
  const [totalEmployeeCount, setTotalEmployeeCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(12);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const searchTimerRef = useRef(null);
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const shiftConfig = DEFAULT_SHIFT;
  const { photo: showPhoto, time: showTime } = config || { photo: true, time: true };

  /* Reset checkout */
  const [resetTarget, setResetTarget] = useState(null);
  /* Punch detail */
  const [punchDetail, setPunchDetail] = useState(null);
  /* Overflow menu */
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuRow, setMenuRow] = useState(null);

  /* Debounced search */
  useEffect(() => {
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setDebouncedSearch(searchText);
      setPage(0);
    }, 300);
    return () => clearTimeout(searchTimerRef.current);
  }, [searchText]);

  /* Fetch data */
  const fetchData = useCallback(() => {
    setLoading(true);
    const params = {
      page: `${page + 1}`,
      limit: rowsPerPage,
      employeeName: debouncedSearch || "",
    };
    AdminCheckInOutHistory(params)
      .then((res) => {
        setLoading(false);
        if (res?.status === 200) {
          const rows = res?.data?.data?.data || [];
          setTableResponse(rows);
          const count = res?.data?.data?.totalCount ?? 0;
          setTotalEmployeeCount(count);
          onTotalCount(count);
        }
      })
      .catch((error) => {
        setLoading(false);
        generateErrorMessage(error?.response?.data?.message || "Something went wrong");
      });
  }, [page, rowsPerPage, debouncedSearch, onTotalCount]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    const { hostname } = window.location;
    document.title = `${hostname} | Monthly Attendance`;
  }, []);

  /* Process rows */
  const processedRows = useMemo(() => {
    return tableResponse.map((row) => {
      const status = deriveStatus(row, shiftConfig);
      const lateMins = status === "late" ? getLateMins(row, shiftConfig) : 0;
      const workedHours = parseHours(row.totalWorkedHours);
      return { ...row, _status: status, _lateMins: lateMins, _workedHours: workedHours };
    });
  }, [tableResponse, shiftConfig]);

  /* Stat counts */
  useEffect(() => {
    const counts = { ontime: 0, late: 0, nocheckout: 0, absent: 0, onleave: 0 };
    processedRows.forEach((r) => { if (counts[r._status] !== undefined) counts[r._status]++; });
    onStatCounts(counts);
  }, [processedRows, onStatCounts]);

  /* Filter */
  const displayRows = useMemo(() => {
    if (!activeFilters.length) return processedRows;
    return processedRows.filter((r) => activeFilters.includes(r._status));
  }, [processedRows, activeFilters]);

  /* Export */
  const handleExport = useCallback(() => {
    setLoading(true);
    AdminCheckInOutHistory({ isExport: true })
      .then((res) => {
        setLoading(false);
        if (res?.status === 200) {
          const rows = res?.data?.data?.data || [];
          const exportData = rows.map(({ empId, firstName, date, checkInTime, checkOutTime, totalWorkedHours }) => ({
            Id: empId, Name: firstName, Date: convertedDate(date),
            Check_In_Time: checkInTime, Check_Out_Time: checkOutTime, Total_Hours: totalWorkedHours,
          }));
          const ws = XLSX.utils.json_to_sheet(exportData);
          const wb = { Sheets: { data: ws }, SheetNames: ["data"] };
          const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
          FileSaver.saveAs(
            new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8" }),
            "Monthly Attendance List.xlsx"
          );
        }
      })
      .catch((error) => {
        setLoading(false);
        generateErrorMessage(error?.response?.data?.message || "Export failed");
      });
  }, []);

  useEffect(() => {
    if (exportRef) exportRef.current = handleExport;
  }, [exportRef, handleExport]);

  /* Reset check-out */
  const handleResetCheckout = useCallback((row) => {
    if (!row?.attendanceId) return;
    setLoading(true);
    resetEmployeeAttendace(row.attendanceId, "checkout")
      .then((res) => {
        if (res?.status === 200) {
          generateSuccessMessage(res?.data?.message || "Check-out reset");
          fetchData();
        }
      })
      .catch((error) => {
        setLoading(false);
        generateErrorMessage(error?.response?.data?.message || "Reset failed");
      });
    setResetTarget(null);
    setPunchDetail(null);
  }, [fetchData]);

  /* Pagination */
  const totalPages = Math.ceil(totalEmployeeCount / rowsPerPage);
  const pageStart = page * rowsPerPage + 1;
  const pageEnd = Math.min((page + 1) * rowsPerPage, totalEmployeeCount);
  const mapsLink = (lat, lng) => `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;

  return (
    <>
      <Box sx={TABLE_CARD_SX}>
        {/* FILTER BAR */}
        <Box sx={{
          p: "16px 18px", borderBottom: `1px solid ${COLORS.hairline}`,
          display: "flex", alignItems: "center", gap: "9px", flexWrap: "wrap",
        }}>
          <Box sx={{
            display: "flex", alignItems: "center", gap: "9px",
            background: "#faf8fd", border: `1px solid ${COLORS.cardBorder}`,
            borderRadius: RADII.control, p: "9px 13px", minWidth: 250, flex: 1, maxWidth: 330,
          }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#a49bb8" strokeWidth="2.2" strokeLinecap="round"><circle cx="11" cy="11" r="7" /><path d="M16.5 16.5L21 21" /></svg>
            <input
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search name or employee ID\u2026"
              style={{ border: "none", outline: "none", background: "transparent", fontFamily: "'DM Sans',sans-serif", fontSize: "14px", color: "#17131f", width: "100%" }}
            />
          </Box>
          {activeFilters.length > 0 && (
            <Box component="span" onClick={onClearFilters} sx={{ fontSize: "13px", fontWeight: 600, color: "#a411c1", cursor: "pointer", p: "9px 6px" }}>Clear</Box>
          )}
          <Box sx={{ flex: 1 }} />
          <Box component="span" sx={{ fontSize: "13px", color: COLORS.muted }}>
            {totalEmployeeCount > 0 ? `${pageStart}\u2013${pageEnd} of ${totalEmployeeCount}` : ""}
          </Box>
        </Box>

        {/* TABLE */}
        <Box sx={{ overflowX: "auto" }}>
          <Box sx={{ minWidth: 1240 }}>
            {/* HEAD */}
            <Box sx={{
              display: "grid", gridTemplateColumns: GRID_COLS, alignItems: "center",
              gap: "14px", p: "12px 18px", borderBottom: `1px solid ${COLORS.hairline}`, background: "#fcfbfe",
            }}>
              {["Employee", "Date", "Status", "In", "Out", "Hours", "Photos", "Geo", "Actions"].map((h) => (
                <Box key={h} component="span" sx={{ ...HEAD_CELL, ...(h === "Actions" ? { textAlign: "center" } : {}) }}>{h}</Box>
              ))}
            </Box>

            {/* LOADING */}
            {loading && !tableResponse.length && (
              Array.from({ length: 6 }).map((_, i) => (
                <Box key={i} sx={{ display: "grid", gridTemplateColumns: GRID_COLS, gap: "14px", p: "14px 18px", borderBottom: "1px solid #f7f4fb" }}>
                  {Array.from({ length: 9 }).map((_, j) => <Skeleton key={j} variant="rounded" height={20} sx={{ borderRadius: "6px" }} />)}
                </Box>
              ))
            )}

            {/* ROWS */}
            {displayRows.map((row, idx) => {
              const tint = TINTS[idx % TINTS.length];
              const initials = getInitials(row.firstName || "");
              const status = row._status;
              const worked = row._workedHours;
              const pct = Math.min(100, (worked / shiftConfig.expectedHours) * 100);
              const hoursColor = worked >= shiftConfig.expectedHours ? COLORS.successText : worked >= shiftConfig.expectedHours * 0.8 ? COLORS.warningText : COLORS.dangerText;
              const barColor = worked >= shiftConfig.expectedHours ? COLORS.success : worked >= shiftConfig.expectedHours * 0.8 ? COLORS.warning : COLORS.danger;
              const lat = row.checkInLocation?.latitude;
              const lng = row.checkInLocation?.longitude;
              const hasLoc = lat != null && lng != null;
              const hasInPhoto = !!row.checkInImagePath;
              const hasOutPhoto = !!row.checkOutImagePath;
              const hasAnyPhoto = hasInPhoto || hasOutPhoto;
              const bothPhotos = hasInPhoto && hasOutPhoto;
              const statusHint = status === "late" ? `${row._lateMins} min late` : status === "nocheckout" ? `open since ${row.checkInTime || ""}` : status === "onleave" ? "approved leave" : "";

              return (
                <Box key={row.attendanceId || idx} className="tt-row" sx={{
                  display: "grid", gridTemplateColumns: GRID_COLS, alignItems: "center",
                  gap: "14px", p: "11px 18px", borderBottom: "1px solid #f7f4fb",
                  transition: "background .12s", ...ROW_HOVER,
                }}>
                  {/* Employee */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: "12px", minWidth: 0 }}>
                    <Box sx={{ width: 36, height: 36, borderRadius: "11px", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: FONTS.heading, fontSize: "13px", fontWeight: 600, background: tint.bg, color: tint.color }}>{initials}</Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Box sx={{ fontSize: "14px", fontWeight: 600, color: COLORS.ink3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", mb: "2px" }}>{row.firstName || "--"}</Box>
                      <Box sx={{ fontSize: "12.5px", color: COLORS.muted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{row.empId || ""}{row.department ? ` \u00b7 ${row.department}` : ""}</Box>
                    </Box>
                  </Box>
                  {/* Date */}
                  <Box>
                    <Box sx={{ fontSize: "13.5px", fontWeight: 500, color: COLORS.ink2 }}>{convertedDate(row.date) || "--"}</Box>
                    <Box sx={{ fontSize: "12px", color: COLORS.muted2 }}>{row.date ? moment(row.date).format("dddd") : ""}</Box>
                  </Box>
                  {/* Status */}
                  <Box>
                    <Box sx={chipSx(status)}>{STATUS_LABELS[status]}</Box>
                    {statusHint && <Box sx={{ fontSize: "12px", color: COLORS.muted2, mt: "5px" }}>{statusHint}</Box>}
                  </Box>
                  {/* In */}
                  <Box sx={{ fontFamily: FONTS.heading, fontSize: "13.5px", fontWeight: showTime && row.checkInTime ? 600 : 400, color: showTime && row.checkInTime ? COLORS.ink2 : COLORS.muted2 }}>{showTime ? (row.checkInTime || "\u2014") : "\u2014"}</Box>
                  {/* Out */}
                  <Box sx={{ fontFamily: FONTS.heading, fontSize: "13.5px", fontWeight: showTime && row.checkOutTime ? 600 : 400, color: showTime && row.checkOutTime ? COLORS.ink2 : status === "nocheckout" ? COLORS.dangerText : COLORS.muted2 }}>{showTime ? (row.checkOutTime || "\u2014") : "\u2014"}</Box>
                  {/* Hours */}
                  <Box>
                    <Box sx={{ display: "flex", alignItems: "baseline", gap: "6px", mb: "6px" }}>
                      <Box component="span" sx={{ fontFamily: FONTS.heading, fontSize: "13.5px", fontWeight: 600, color: hoursColor }}>{worked > 0 ? `${Math.floor(worked)}h ${String(Math.round((worked % 1) * 60)).padStart(2, "0")}m` : "\u2014"}</Box>
                      <Box component="span" sx={{ fontSize: "11.5px", color: "#b3aac4" }}>{worked > 0 ? `/ ${shiftConfig.expectedHours}h` : ""}</Box>
                    </Box>
                    {worked > 0 && (
                      <Box sx={{ height: 4, borderRadius: "999px", background: "#f1edf8", overflow: "hidden" }}>
                        <Box sx={{ height: "100%", borderRadius: "999px", width: `${pct}%`, background: barColor }} />
                      </Box>
                    )}
                  </Box>
                  {/* Photos */}
                  <Box onClick={showPhoto && hasAnyPhoto ? () => setPunchDetail(row) : undefined} sx={{ display: "flex", alignItems: "center", cursor: showPhoto && hasAnyPhoto ? "pointer" : "default", position: "relative" }}>
                    {showPhoto && hasAnyPhoto ? (
                      <>
                        <Box sx={{ width: 28, height: 28, borderRadius: "9px", border: "2px solid #fff", overflow: "hidden", boxShadow: "0 2px 6px -2px rgba(70,30,110,0.3)", position: "relative", zIndex: 2 }}>
                          <img src={hasInPhoto ? `${baseURL}${row.checkInImagePath}` : PHOTO_PLACEHOLDER} alt="in" style={{ width: "100%", height: "100%", objectFit: "cover" }} onError={(e) => { e.target.src = PHOTO_PLACEHOLDER; }} />
                        </Box>
                        <Box sx={{ width: 28, height: 28, borderRadius: "9px", border: "2px solid #fff", overflow: "hidden", ml: "-8px", position: "relative", zIndex: 1 }}>
                          <img src={hasOutPhoto ? `${baseURL}${row.checkOutImagePath}` : PHOTO_PLACEHOLDER} alt="out" style={{ width: "100%", height: "100%", objectFit: "cover" }} onError={(e) => { e.target.src = PHOTO_PLACEHOLDER; }} />
                        </Box>
                        <Box sx={{ position: "absolute", bottom: -2, right: 10, width: 14, height: 14, borderRadius: "50%", background: bothPhotos ? COLORS.success : COLORS.warning, display: "flex", alignItems: "center", justifyContent: "center", border: "1.5px solid #fff" }}>
                          <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="4" strokeLinecap="round"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>
                        </Box>
                      </>
                    ) : (
                      <Box component="span" sx={{ fontSize: "13px", color: "#c9c2da" }}>{"\u2014"}</Box>
                    )}
                  </Box>
                  {/* Geo */}
                  <Box sx={{ display: "flex", justifyContent: "center" }}>
                    <Tooltip title={hasLoc ? `Lat: ${lat}, Lng: ${lng}` : "Location not found"}>
                      {hasLoc ? (
                        <a href={mapsLink(lat, lng)} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>
                          <Box sx={{ width: 30, height: 30, borderRadius: "8px", border: `1px solid ${COLORS.cardBorder}`, display: "flex", alignItems: "center", justifyContent: "center", color: COLORS.success }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M12 21s7-6.3 7-11a7 7 0 10-14 0c0 4.7 7 11 7 11z" /><circle cx="12" cy="10" r="2.4" /></svg>
                          </Box>
                        </a>
                      ) : (
                        <Box sx={{ width: 30, height: 30, borderRadius: "8px", border: `1px solid ${COLORS.cardBorder}`, display: "flex", alignItems: "center", justifyContent: "center", color: COLORS.muted2 }}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M12 21s7-6.3 7-11a7 7 0 10-14 0c0 4.7 7 11 7 11z" /><circle cx="12" cy="10" r="2.4" /></svg>
                        </Box>
                      )}
                    </Tooltip>
                  </Box>
                  {/* Actions */}
                  <Box className="tt-actions" sx={{ display: "flex", justifyContent: "center", gap: "5px", opacity: 0.35, transition: "opacity .14s" }}>
                    <Box component="span" onClick={(e) => { setMenuAnchor(e.currentTarget); setMenuRow(row); }} sx={{ width: 30, height: 30, borderRadius: "8px", border: `1px solid ${COLORS.cardBorder}`, background: COLORS.surface, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", "&:hover": { background: "#faf5ff", borderColor: "#e7d9f5" } }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="#6a637d"><circle cx="5" cy="12" r="1.7" /><circle cx="12" cy="12" r="1.7" /><circle cx="19" cy="12" r="1.7" /></svg>
                    </Box>
                  </Box>
                </Box>
              );
            })}

            {/* Empty */}
            {!loading && displayRows.length === 0 && (
              <Box sx={{ p: "66px 24px", textAlign: "center" }}>
                <Box sx={{ width: 52, height: 52, m: "0 auto 16px", borderRadius: "15px", background: "#f7f4fb", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#b3aac4" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="7" /><path d="M16.5 16.5L21 21" /></svg>
                </Box>
                <Box sx={{ fontFamily: FONTS.heading, fontSize: "17px", fontWeight: 600, color: COLORS.ink, mb: "6px" }}>No records found</Box>
                <Box sx={{ fontSize: "14px", color: COLORS.muted, mb: "20px" }}>{activeFilters.length ? "Try removing filters." : "No attendance data for this month."}</Box>
                {activeFilters.length > 0 && (
                  <Box component="span" onClick={onClearFilters} sx={{ display: "inline-flex", fontSize: "13.5px", fontWeight: 600, color: "#a411c1", background: "rgba(224,89,240,0.09)", border: "1px solid rgba(224,89,240,0.22)", p: "9px 17px", borderRadius: "9px", cursor: "pointer" }}>Clear all filters</Box>
                )}
              </Box>
            )}
          </Box>
        </Box>

        {/* PAGINATION */}
        <Box sx={{ p: "14px 18px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "14px", flexWrap: "wrap" }}>
          <Box component="span" sx={{ fontSize: "13px", color: COLORS.muted }}>{totalEmployeeCount > 0 ? `${pageStart}\u2013${pageEnd} of ${totalEmployeeCount}` : "No results"}</Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: "14px" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <Box component="span" sx={{ fontSize: "13px", color: COLORS.muted }}>Rows</Box>
              {[12, 25, 50].map((size) => (
                <Box key={size} component="span" onClick={() => { setRowsPerPage(size); setPage(0); }} sx={{
                  fontSize: "13px", fontWeight: rowsPerPage === size ? 700 : 500,
                  color: rowsPerPage === size ? "#a411c1" : COLORS.muted,
                  background: rowsPerPage === size ? "rgba(224,89,240,0.09)" : "transparent",
                  p: "4px 9px", borderRadius: "7px", cursor: "pointer",
                  border: rowsPerPage === size ? "1px solid rgba(224,89,240,0.22)" : "1px solid transparent",
                }}>{size}</Box>
              ))}
            </Box>
            <Box sx={{ display: "flex", gap: "6px" }}>
              <Box component="span" onClick={page > 0 ? () => setPage(page - 1) : undefined} sx={{ width: 32, height: 32, borderRadius: "9px", border: `1px solid ${COLORS.cardBorder}`, background: COLORS.surface, display: "flex", alignItems: "center", justifyContent: "center", cursor: page > 0 ? "pointer" : "not-allowed", opacity: page > 0 ? 1 : 0.4 }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><path d="M14 6l-6 6 6 6" /></svg>
              </Box>
              <Box component="span" onClick={page < totalPages - 1 ? () => setPage(page + 1) : undefined} sx={{ width: 32, height: 32, borderRadius: "9px", border: `1px solid ${COLORS.cardBorder}`, background: COLORS.surface, display: "flex", alignItems: "center", justifyContent: "center", cursor: page < totalPages - 1 ? "pointer" : "not-allowed", opacity: page < totalPages - 1 ? 1 : 0.4 }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><path d="M10 6l6 6-6 6" /></svg>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Overflow menu */}
      <Popover open={Boolean(menuAnchor)} anchorEl={menuAnchor} onClose={() => { setMenuAnchor(null); setMenuRow(null); }} anchorOrigin={{ vertical: "bottom", horizontal: "right" }} transformOrigin={{ vertical: "top", horizontal: "right" }} PaperProps={{ sx: { borderRadius: "12px", border: `1px solid ${COLORS.cardBorder}`, boxShadow: "0 24px 48px -22px rgba(70,30,110,0.4)", p: "6px", minWidth: 180 } }}>
        <Box onClick={() => { navigate(`/employees/details/${menuRow?.employeeId}`); setMenuAnchor(null); }} sx={{ p: "9px 11px", borderRadius: "9px", fontSize: "13.5px", cursor: "pointer", "&:hover": { background: "#f8f5fd" } }}>View profile</Box>
        {/* <Box onClick={() => { if (menuRow?.checkOutTime) setResetTarget(menuRow); setMenuAnchor(null); }} sx={{ p: "9px 11px", borderRadius: "9px", fontSize: "13.5px", cursor: menuRow?.checkOutTime ? "pointer" : "not-allowed", color: menuRow?.checkOutTime ? COLORS.warningText : COLORS.muted2, "&:hover": menuRow?.checkOutTime ? { background: "#fdf3e5" } : {} }}>Reset check-out</Box> */}
      </Popover>

      {/* Reset dialog */}
      <Dialog open={Boolean(resetTarget)} onClose={() => setResetTarget(null)} PaperProps={{ sx: { borderRadius: "20px", maxWidth: 440 } }}>
        <Box sx={{ p: "20px 24px 18px", borderBottom: `1px solid ${COLORS.hairline}` }}>
          <Box sx={{ fontFamily: FONTS.heading, fontSize: "18px", fontWeight: 600, color: COLORS.ink, mb: "4px" }}>Reset check-out</Box>
          <Box sx={{ fontSize: "13px", color: COLORS.muted }}>This will remove the check-out record for {resetTarget?.firstName} ({resetTarget?.empId}). This action cannot be undone.</Box>
        </Box>
        <Box sx={{ p: "16px 24px", display: "flex", justifyContent: "flex-end", gap: "10px" }}>
          <Box component="button" onClick={() => setResetTarget(null)} sx={{ fontSize: "13.5px", fontWeight: 500, color: COLORS.body, background: COLORS.surface, border: `1px solid ${COLORS.cardBorder}`, p: "9px 15px", borderRadius: RADII.control, cursor: "pointer" }}>Cancel</Box>
          <Box component="button" onClick={() => handleResetCheckout(resetTarget)} sx={{ fontSize: "13.5px", fontWeight: 600, color: "#9b5a06", background: "#fdf3e5", border: "1px solid #f6e2c1", p: "9px 15px", borderRadius: RADII.control, cursor: "pointer" }}>Reset check-out</Box>
        </Box>
      </Dialog>

      {/* Punch detail modal */}
      <Dialog open={Boolean(punchDetail)} onClose={() => setPunchDetail(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "20px", overflow: "hidden" } }}>
        {punchDetail && (() => {
          const row = punchDetail;
          const tint = TINTS[0];
          const initials = getInitials(row.firstName || "");
          return (
            <>
              <Box sx={{ p: "20px 24px", borderBottom: `1px solid ${COLORS.hairline}`, display: "flex", alignItems: "center", gap: "14px" }}>
                <Box sx={{ width: 42, height: 42, borderRadius: "12px", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: FONTS.heading, fontSize: "14px", fontWeight: 600, background: tint.bg, color: tint.color }}>{initials}</Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ fontFamily: FONTS.heading, fontSize: "17px", fontWeight: 600, color: COLORS.ink }}>{row.firstName}</Box>
                  <Box sx={{ fontSize: "13px", color: COLORS.muted }}>{row.empId} {"\u00b7"} {convertedDate(row.date)}</Box>
                </Box>
                <Box component="button" onClick={() => setPunchDetail(null)} sx={{ width: 32, height: 32, borderRadius: "9px", border: `1px solid ${COLORS.cardBorder}`, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", background: "transparent" }}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#6a637d" strokeWidth="2.2" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
                </Box>
              </Box>
              <Box sx={{ p: "22px 24px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
                <Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: "9px" }}>
                    <Box component="span" sx={{ fontSize: "12.5px", fontWeight: 600, letterSpacing: "0.05em", textTransform: "uppercase", color: COLORS.muted }}>Check-in</Box>
                    <Box component="span" sx={{ fontFamily: FONTS.heading, fontSize: "14px", fontWeight: 600, color: COLORS.ink3 }}>{row.checkInTime || "\u2014"}</Box>
                  </Box>
                  <Box sx={{ aspectRatio: "4/5", borderRadius: "14px", border: `1px solid ${COLORS.cardBorder}`, overflow: "hidden", background: "linear-gradient(150deg,#f8f4fd,#f1edfa)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    {row.checkInImagePath ? <img src={`${baseURL}${row.checkInImagePath}`} alt="Check-in" style={{ width: "100%", height: "100%", objectFit: "cover" }} onError={(e) => { e.target.src = PHOTO_PLACEHOLDER; }} /> : <Box sx={{ color: COLORS.muted2, fontSize: "12.5px" }}>No check-in photo</Box>}
                  </Box>
                </Box>
                <Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: "9px" }}>
                    <Box component="span" sx={{ fontSize: "12.5px", fontWeight: 600, letterSpacing: "0.05em", textTransform: "uppercase", color: COLORS.muted }}>Check-out</Box>
                    <Box component="span" sx={{ fontFamily: FONTS.heading, fontSize: "14px", fontWeight: 600, color: row.checkOutTime ? COLORS.ink3 : COLORS.warningText }}>{row.checkOutTime || "\u2014"}</Box>
                  </Box>
                  <Box sx={{ aspectRatio: "4/5", borderRadius: "14px", border: `1px solid ${COLORS.cardBorder}`, overflow: "hidden", background: "linear-gradient(150deg,#f8f4fd,#f1edfa)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    {row.checkOutImagePath ? <img src={`${baseURL}${row.checkOutImagePath}`} alt="Check-out" style={{ width: "100%", height: "100%", objectFit: "cover" }} onError={(e) => { e.target.src = PHOTO_PLACEHOLDER; }} /> : <Box sx={{ textAlign: "center" }}><Box sx={{ fontSize: "12.5px", color: "#9b5a06", fontWeight: 500 }}>No check-out recorded</Box></Box>}
                  </Box>
                </Box>
              </Box>
              <Box sx={{ p: "16px 24px 22px", display: "flex", alignItems: "center", gap: "10px", borderTop: `1px solid ${COLORS.hairline}` }}>
                <Box sx={{ flex: 1 }} />
                <Box component="button" onClick={() => { setPunchDetail(null); navigate(`/employees/details/${row.employeeId}`); }} sx={{ fontSize: "13.5px", fontWeight: 500, color: COLORS.body, background: COLORS.surface, border: `1px solid ${COLORS.cardBorder}`, p: "9px 15px", borderRadius: RADII.control, cursor: "pointer" }}>View profile</Box>
                <Box component="button" onClick={row.checkOutTime ? () => handleResetCheckout(row) : undefined} sx={{ fontSize: "13.5px", fontWeight: 600, color: "#9b5a06", background: "#fdf3e5", border: "1px solid #f6e2c1", p: "9px 15px", borderRadius: RADII.control, cursor: row.checkOutTime ? "pointer" : "not-allowed", opacity: row.checkOutTime ? 1 : 0.5 }}>Reset check-out</Box>
              </Box>
            </>
          );
        })()}
      </Dialog>
    </>
  );
};

export default MonthlyList;
